import * as v from 'valibot'

import { HOUR } from '@/core/constants/constants'
import { authConfig } from '@/core/keycloak'
import { APP_LOCALE } from '@/locale/constants'

import { createApiMethod, type } from './utils/createApiMethod'

/**
 * Base URL for the Keycloak account API
 */
const baseURL = `${authConfig.url}/realms/${authConfig.realm}`

const UserSchema = v.object({
  firstName: v.string(),
  lastName: v.string(),
  email: v.string(),
  emailVerified: v.boolean(),
  userProfileMetadata: v.object({
    attributes: v.array(
      v.object({
        name: v.string(),
        displayName: v.string(),
        required: v.boolean(),
        readOnly: v.boolean(),
        annotations: v.optional(v.record(v.string(), v.any())),
        validators: v.record(v.string(), v.record(v.string(), v.any())),
        multivalued: v.optional(v.boolean()),
      }),
    ),
  }),
  attributes: v.object({
    company_id: v.optional(v.array(v.string())),
    legacy_user_id: v.optional(v.array(v.string())),
    phone: v.optional(v.array(v.string())),
    date_of_birth: v.optional(v.array(v.string())),
    title: v.optional(v.array(v.string())),
    locale: v.optional(v.array(v.picklist(APP_LOCALE))),
    department: v.optional(v.array(v.string())),
    job_title: v.optional(v.array(v.string())),
  }),
})

const CredentialsContainerSchema = v.object({
  type: v.union([v.literal('password'), v.literal('otp')]),
  category: v.union([
    v.literal('basic-authentication'),
    v.literal('two-factor'),
    v.literal('passwordless'),
  ]),
  updateAction: v.optional(v.string()),
  createAction: v.optional(v.string()),
  removeable: v.boolean(),
  userCredentialMetadatas: v.array(
    v.object({
      credential: v.object({
        id: v.string(),
        type: v.union([v.literal('password'), v.literal('otp')]),
        userLabel: v.optional(v.string()),
        createdDate: v.number(),
        credentialData: v.string(),
      }),
    }),
  ),
})

type User = v.InferOutput<typeof UserSchema>

export const keycloak = {
  account: {
    details: createApiMethod({
      config: { baseURL },
      method: 'GET',
      path: '/account',
      response: UserSchema,
    }),
    update: createApiMethod({
      config: { baseURL },
      method: 'POST',
      path: '/account',
      data: type<User>(),
    }),
    credentials: {
      details: createApiMethod({
        config: { baseURL },
        method: 'GET',
        path: '/account/credentials',
        response: v.array(CredentialsContainerSchema),
      }),
      delete: createApiMethod({
        config: { baseURL },
        method: 'DELETE',
        path: '/account/credentials/:credentialId',
      }),
    },
  },
}

export const keycloakQueries = {
  all: () => ['keycloak', 'account'] as const,
  detail: () => ({
    queryKey: [...keycloakQueries.all(), 'detail'] as const,
    queryFn: () => keycloak.account.details(),
    staleTime: HOUR,
  }),
  credentials: () => ({
    queryKey: [...keycloakQueries.all(), 'credentials'] as const,
    queryFn: () => keycloak.account.credentials.details(),
    staleTime: HOUR,
  }),
}
